import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { assetUrl } from "../../lib/api";

/**
 * Immersive full-screen guided photo walk-through of a show home.
 * (Cinematic photo tour — not a 360° panorama.)
 */
const ShowHomeTour = ({ images = [], title = "Show Home Tour", open, onClose }) => {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const n = images.length;

  const go = useCallback((d) => setI((c) => (c + d + n) % n), [n]);

  useEffect(() => { if (open) { setI(0); setPlaying(true); } }, [open]);

  useEffect(() => {
    if (!open || !playing || n <= 1) return;
    const t = setInterval(() => setI((c) => (c + 1) % n), 3800);
    return () => clearInterval(t);
  }, [open, playing, n]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, go, onClose]);

  if (!open || !n) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-[#0C1510]" data-testid="showhome-tour">
      {/* stacked images for cross-fade */}
      {images.map((src, idx) => (
        <img
          key={idx}
          src={assetUrl(src)}
          alt={`${title} — photo ${idx + 1} of ${n}`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-in-out"
          style={{ opacity: idx === i ? 1 : 0 }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0C1510]/85 via-transparent to-[#0C1510]/55 pointer-events-none" />

      {/* top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between p-5 md:p-7 z-10">
        <div>
          <p className="eyebrow text-[#C9B79C] text-[0.55rem]">Immersive photo tour</p>
          <p className="font-display text-xl md:text-2xl text-[#F3EEE3]">{title}</p>
        </div>
        <button onClick={onClose} data-testid="tour-close" className="w-11 h-11 rounded-full bg-[#12201A]/70 border border-[#F3EEE3]/20 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><X size={20} /></button>
      </div>

      {/* arrows */}
      <button onClick={() => { setPlaying(false); go(-1); }} data-testid="tour-prev" className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-[#12201A]/60 border border-[#F3EEE3]/20 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><ChevronLeft size={24} /></button>
      <button onClick={() => { setPlaying(false); go(1); }} data-testid="tour-next" className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-[#12201A]/60 border border-[#F3EEE3]/20 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><ChevronRight size={24} /></button>

      {/* bottom controls */}
      <div className="absolute bottom-0 inset-x-0 p-5 md:p-7 z-10 flex items-center justify-between gap-4">
        <button onClick={() => setPlaying((p) => !p)} data-testid="tour-play" className="inline-flex items-center gap-2 text-[#F3EEE3] text-sm bg-[#12201A]/70 border border-[#F3EEE3]/20 rounded-full px-4 py-2 hover:bg-[#12201A]">
          {playing ? <Pause size={15} /> : <Play size={15} />} {playing ? "Pause" : "Play"}
        </button>
        <div className="flex-1 flex items-center gap-1.5 justify-center flex-wrap">
          {images.map((_, idx) => (
            <button key={idx} onClick={() => { setPlaying(false); setI(idx); }} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-[#C9B79C]" : "w-4 bg-[#F3EEE3]/25"}`} />
          ))}
        </div>
        <span className="text-[#F3EEE3]/70 text-sm tabular-nums">{i + 1} / {n}</span>
      </div>
    </div>
  );
};

export default ShowHomeTour;
